import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  Workspace,
  WorkspaceMember,
  WorkspaceInvitation,
  WorkspaceProject,
  WorkspaceDashboard,
} from '@/types';

/**
 * Workspace state store using Zustand
 * Manages workspace-related state for the project management app
 */

interface WorkspaceState {
  // Workspace lists
  workspaces: Workspace[];

  // Selected/active workspace
  selectedWorkspace: Workspace | null;
  workspaceMembers: WorkspaceMember[];
  workspaceProjects: WorkspaceProject[];
  workspaceDashboard: WorkspaceDashboard | null;

  // Invitations
  receivedInvitations: WorkspaceInvitation[];
  workspaceInvitations: WorkspaceInvitation[]; // Pending invitations for selected workspace

  // Loading states
  isLoading: boolean;
  isLoadingMembers: boolean;
  isLoadingProjects: boolean;
  isLoadingDashboard: boolean;
  isLoadingInvitations: boolean;
}

interface WorkspaceActions {
  // Workspace list actions
  setWorkspaces: (workspaces: Workspace[]) => void;
  addWorkspace: (workspace: Workspace) => void;
  updateWorkspace: (workspaceId: string, updates: Partial<Workspace>) => void;
  removeWorkspace: (workspaceId: string) => void;

  // Selection actions
  setSelectedWorkspace: (workspace: Workspace | null) => void;

  // Members actions
  setWorkspaceMembers: (members: WorkspaceMember[]) => void;
  addWorkspaceMember: (member: WorkspaceMember) => void;
  removeWorkspaceMember: (userId: string) => void;
  updateMemberRole: (userId: string, role: WorkspaceMember['role']) => void;

  // Projects actions
  setWorkspaceProjects: (projects: WorkspaceProject[]) => void;

  // Dashboard actions
  setWorkspaceDashboard: (dashboard: WorkspaceDashboard | null) => void;

  // Invitations actions
  setReceivedInvitations: (invitations: WorkspaceInvitation[]) => void;
  setWorkspaceInvitations: (invitations: WorkspaceInvitation[]) => void;
  removeReceivedInvitation: (invitationId: string) => void;
  removeWorkspaceInvitation: (invitationId: string) => void;

  // Loading actions
  setLoading: (isLoading: boolean) => void;
  setLoadingMembers: (isLoading: boolean) => void;
  setLoadingProjects: (isLoading: boolean) => void;
  setLoadingDashboard: (isLoading: boolean) => void;
  setLoadingInvitations: (isLoading: boolean) => void;

  // Clear all workspace data
  clearWorkspaces: () => void;
}

type WorkspaceStore = WorkspaceState & WorkspaceActions;

export const useWorkspaceStore = create<WorkspaceStore>()(
  persist(
    (set) => ({
      // Initial state
      workspaces: [],
      selectedWorkspace: null,
      workspaceMembers: [],
      workspaceProjects: [],
      workspaceDashboard: null,
      receivedInvitations: [],
      workspaceInvitations: [],
      isLoading: false,
      isLoadingMembers: false,
      isLoadingProjects: false,
      isLoadingDashboard: false,
      isLoadingInvitations: false,

      // Workspace list actions
      setWorkspaces: (workspaces: Workspace[]) => {
        set({ workspaces, isLoading: false });
      },

      addWorkspace: (workspace: Workspace) => {
        set((state) => ({
          workspaces: [workspace, ...state.workspaces],
        }));
      },

      updateWorkspace: (workspaceId: string, updates: Partial<Workspace>) => {
        set((state) => {
          const updatedWorkspaces = state.workspaces.map((workspace) =>
            workspace.workspaceId === workspaceId
              ? { ...workspace, ...updates }
              : workspace
          );

          return {
            workspaces: updatedWorkspaces,
            selectedWorkspace:
              state.selectedWorkspace?.workspaceId === workspaceId
                ? { ...state.selectedWorkspace, ...updates }
                : state.selectedWorkspace,
          };
        });
      },

      removeWorkspace: (workspaceId: string) => {
        set((state) => ({
          workspaces: state.workspaces.filter(
            (workspace) => workspace.workspaceId !== workspaceId
          ),
          selectedWorkspace:
            state.selectedWorkspace?.workspaceId === workspaceId
              ? null
              : state.selectedWorkspace,
        }));
      },

      // Selection actions
      setSelectedWorkspace: (workspace: Workspace | null) => {
        set((state) => {
          // Don't clear data if selecting the same workspace
          const isSameWorkspace =
            workspace?.workspaceId === state.selectedWorkspace?.workspaceId;

          if (isSameWorkspace) {
            // Just update workspace details, keep existing data
            return { selectedWorkspace: workspace };
          }

          // Clear related data when changing to a different workspace
          return {
            selectedWorkspace: workspace,
            workspaceMembers: [],
            workspaceProjects: [],
            workspaceDashboard: null,
            workspaceInvitations: [],
          };
        });
      },

      // Members actions
      setWorkspaceMembers: (members: WorkspaceMember[]) => {
        set({ workspaceMembers: members, isLoadingMembers: false });
      },

      addWorkspaceMember: (member: WorkspaceMember) => {
        set((state) => ({
          workspaceMembers: [...state.workspaceMembers, member],
        }));
      },

      removeWorkspaceMember: (userId: string) => {
        set((state) => ({
          workspaceMembers: state.workspaceMembers.filter(
            (member) => member.userId !== userId
          ),
        }));
      },

      updateMemberRole: (userId: string, role: WorkspaceMember['role']) => {
        set((state) => ({
          workspaceMembers: state.workspaceMembers.map((member) =>
            member.userId === userId ? { ...member, role } : member
          ),
        }));
      },

      // Projects actions
      setWorkspaceProjects: (projects: WorkspaceProject[]) => {
        set({ workspaceProjects: projects, isLoadingProjects: false });
      },

      // Dashboard actions
      setWorkspaceDashboard: (dashboard: WorkspaceDashboard | null) => {
        set({ workspaceDashboard: dashboard, isLoadingDashboard: false });
      },

      // Invitations actions
      setReceivedInvitations: (invitations: WorkspaceInvitation[]) => {
        set({ receivedInvitations: invitations, isLoadingInvitations: false });
      },

      setWorkspaceInvitations: (invitations: WorkspaceInvitation[]) => {
        set({ workspaceInvitations: invitations });
      },

      removeReceivedInvitation: (invitationId: string) => {
        set((state) => ({
          receivedInvitations: state.receivedInvitations.filter(
            (inv) => inv.workspaceInvitationId !== invitationId
          ),
        }));
      },

      removeWorkspaceInvitation: (invitationId: string) => {
        set((state) => ({
          workspaceInvitations: state.workspaceInvitations.filter(
            (inv) => inv.workspaceInvitationId !== invitationId
          ),
        }));
      },

      // Loading actions
      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },

      setLoadingMembers: (isLoading: boolean) => {
        set({ isLoadingMembers: isLoading });
      },

      setLoadingProjects: (isLoading: boolean) => {
        set({ isLoadingProjects: isLoading });
      },

      setLoadingDashboard: (isLoading: boolean) => {
        set({ isLoadingDashboard: isLoading });
      },

      setLoadingInvitations: (isLoading: boolean) => {
        set({ isLoadingInvitations: isLoading });
      },

      // Clear all workspace data
      clearWorkspaces: () => {
        set({
          workspaces: [],
          selectedWorkspace: null,
          workspaceMembers: [],
          workspaceProjects: [],
          workspaceDashboard: null,
          receivedInvitations: [],
          workspaceInvitations: [],
          isLoading: false,
          isLoadingMembers: false,
          isLoadingProjects: false,
          isLoadingDashboard: false,
          isLoadingInvitations: false,
        });
      },
    }),
    {
      name: 'workspace-storage',
      partialize: (state) => ({
        selectedWorkspace: state.selectedWorkspace,
      }),
    }
  )
);

/**
 * Selector hooks for specific state slices
 */
export const useWorkspaces = () => useWorkspaceStore((state) => state.workspaces);
export const useSelectedWorkspace = () => useWorkspaceStore((state) => state.selectedWorkspace);
export const useWorkspaceMembers = () => useWorkspaceStore((state) => state.workspaceMembers);
export const useWorkspaceProjects = () => useWorkspaceStore((state) => state.workspaceProjects);
export const useWorkspaceDashboard = () => useWorkspaceStore((state) => state.workspaceDashboard);
export const useReceivedWorkspaceInvitations = () =>
  useWorkspaceStore((state) => state.receivedInvitations);
export const useWorkspaceInvitations = () =>
  useWorkspaceStore((state) => state.workspaceInvitations);
export const useWorkspaceLoading = () => useWorkspaceStore((state) => state.isLoading);
export const useWorkspaceProjectsLoading = () => useWorkspaceStore((state) => state.isLoadingProjects);

export default useWorkspaceStore;
